// Compatibility layer for LandingSounds
// This file provides backward compatibility while using the new sound manager system

import soundsConfig from "../../data/sounds-config.json"
import {
  getSoundPath,
  playOptimizedAudio as playOptimizedAudioNew,
  setGlobalMute as setGlobalMuteNew,
} from "../../hooks/use-sound-manager"

// Helper to build full URL
function getSoundUrl(path: string): string {
  if (path.startsWith("http")) return path
  const basePath = path.startsWith("/") ? path.slice(1) : path
  return `${soundsConfig.baseUrl}/${basePath}`
}

// Export intro clips for backward compatibility
export const introBasketballClip = getSoundUrl("calls/intros/intro-basketball.mp3")
export const introGolfClip = getSoundUrl("calls/intros/intro-golf.mp3")
export const introTennisClip = getSoundUrl("calls/intros/intro-tennis.mp3")
export const introHockeyClip = getSoundUrl("calls/intros/intro-hockey.mp3")
export const introBaseballClip = getSoundUrl("calls/intros/intro-baseball.mp3")
export const introFootballClip = getSoundUrl("calls/intros/intro-football-11.mp3")

// Export fight song tracks
const fightSongPaths = getSoundPath("themes", "fightSongs") as string[]
export const fightSongTracks = fightSongPaths.map((path) => getSoundUrl(path))

// Export bracky notification clips
const brackyPaths = getSoundPath("bracky", "notifications") as string[]
export const brackyNotificationClips = brackyPaths.map((path) => getSoundUrl(path))

// Global mute state wrapper
export const setGlobalMute = setGlobalMuteNew

// Remove the hook usage - hooks can't be used outside React components
// Use the exported functions directly instead

// Backward compatibility functions
let lastAudioToggleSoundTime = 0
export function playAudioToggleSound() {
  const now = Date.now()
  if (now - lastAudioToggleSoundTime < 1) {
    return
  }
  lastAudioToggleSoundTime = now

  const path = getSoundPath("bracket", "select", "click") as string
  const soundUrl = path ? getSoundUrl(path) : ""
  if (soundUrl) playOptimizedAudioNew(soundUrl, 0.6, true, true)
}

let lastErrorSoundTime = 0
export function playBracketErrorSound() {
  const now = Date.now()
  if (now - lastErrorSoundTime < 1) {
    return
  }
  lastErrorSoundTime = now

  const path = getSoundPath("bracket", "select", "error") as string
  const soundUrl = path ? getSoundUrl(path) : ""
  if (soundUrl) playOptimizedAudioNew(soundUrl, 0.7, true)
}

let lastCardOpenSoundTime = 0
export function playBracketCardOpenSound() {
  const now = Date.now()
  if (now - lastCardOpenSoundTime < 1) {
    return
  }
  lastCardOpenSoundTime = now

  const path = getSoundPath("bracket", "card", "open") as string
  const soundUrl = path ? getSoundUrl(path) : ""
  if (soundUrl) playOptimizedAudioNew(soundUrl, 0.8, true)
}

let lastClickSoundTime = 0
export function playBracketClickSound(selectionCount = 1) {
  const now = Date.now()
  if (now - lastClickSoundTime < 1) {
    return
  }
  lastClickSoundTime = now

  const clickPaths = getSoundPath("bracket", "select", "clicks") as string[]
  const clamped = Math.min(Math.max(selectionCount, 1), clickPaths.length)
  const clickPath = clickPaths[clamped - 1]
  const soundSrc = clickPath ? getSoundUrl(clickPath) : ""
  if (soundSrc) playOptimizedAudioNew(soundSrc, 0.7, true)
}

let lastDeselectSoundTime = 0
export function playBracketDeselectSound() {
  const now = Date.now()
  if (now - lastDeselectSoundTime < 1) {
    return
  }
  lastDeselectSoundTime = now

  const path = getSoundPath("bracket", "select", "deselect") as string
  const soundUrl = path ? getSoundUrl(path) : ""
  if (soundUrl) playOptimizedAudioNew(soundUrl, 0.7, true)
}

export function playSingleAudio(src: string, volume = 1, allowOverlay = false) {
  playOptimizedAudioNew(src, volume, allowOverlay)
}

export function getBrackyNotificationClip(): string {
  const randomIndex = Math.floor(Math.random() * brackyNotificationClips.length)
  const clip = brackyNotificationClips[randomIndex]
  return clip ?? ""
}

export function getSloganClip(): string {
  const sloganPaths = getSoundPath("slogans", "regular") as string[]
  const randomIndex = Math.floor(Math.random() * sloganPaths.length)
  const sloganPath = sloganPaths[randomIndex]
  return sloganPath ? getSoundUrl(sloganPath) : ""
}

export function getClipForSport(sport: string): string | null {
  if (sport === "golf") {
    const path = getSoundPath("calls", "golf") as string
    return path ? getSoundUrl(path) : null
  }
  if (sport === "basketball") {
    const paths = getSoundPath("calls", "basketball") as string[]
    const idx = Math.floor(Math.random() * paths.length)
    const basketballPath = paths[idx]
    return basketballPath ? getSoundUrl(basketballPath) : null
  }
  if (sport === "football") {
    const footballTrack = fightSongTracks[0]
    return footballTrack ?? null
  }
  return null
}
